import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Loader2, AlertTriangle, RefreshCw, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { ensureFreshRuntimeAtBoot } from '../utils/runtimeFreshness';
import { consumeIntendedRoute } from '../utils/postAuthRedirect';
import roleHome from '../utils/roleHome';

/**
 * AuthCallback — Iter-411s hardened.
 *
 * Google (via Emergent Auth) lands here with a `#session_id=…` fragment.
 * Facebook lands here with `?code=…&state=…` query params.
 *
 * Contract:
 *   The "Welcome back!" success toast + navigation MUST NOT fire until
 *   EVERY one of these is true:
 *     • The OAuth exchange returned a token + user.
 *     • Token was persisted to localStorage.
 *     • Existing iEvntür account resolved (backend enforces email-only
 *       match — never creates a duplicate).
 *     • /me bootstrap returned a full user shape.
 *     • Role was resolved for redirect.
 *     • Verification gating (idv_status / client_approval_status) is
 *       honoured so gated users land on the correct pending screen.
 *
 * On any failure we render a recoverable error state with:
 *     • Retry — retries just the /me bootstrap (token is preserved).
 *     • Sign out & retry — full sign-out and back to /login.
 * We NEVER strand the user silently on the welcome screen.
 */
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithSocial, logout } = useAuth();
  const [status, setStatus] = useState('Signing you in…');
  const [errorState, setErrorState] = useState(null); // { message, canRetry }
  // useRef (not useState) so StrictMode double-mount does not double-fire.
  const processed = useRef(false);

  const routeByUser = useCallback((user) => {
    if (!user || !user.role) {
      throw new Error('User role missing after bootstrap');
    }
    const role = user.role;
    // A tapped deep link (e.g. a job) must survive social login too. When the
    // user is in a viewable state we send them straight to it; when a gate
    // (pending approval / IDV) applies we route to the gate but LEAVE the
    // destination stored so they return to it after clearing the gate.
    if (role === 'admin' || role === 'super_admin') {
      navigate('/admin/home', { replace: true });
      return;
    }
    if (role === 'client' || role === 'agency') {
      const s = user.client_approval_status;
      if (s && s !== 'approved') {
        navigate('/client/pending', { replace: true });
      } else {
        navigate(consumeIntendedRoute() || (role === 'agency' ? '/agency/dashboard' : '/client/home'), { replace: true });
      }
      return;
    }
    if (role === 'talent') {
      if (user.idv_status && user.idv_status !== 'approved') {
        navigate('/talent/idv', { replace: true });
      } else {
        navigate(consumeIntendedRoute() || '/talent/home', { replace: true });
      }
      return;
    }
    if (role === 'ai_creator') {
      navigate('/creator/dashboard', { replace: true });
      return;
    }
    if (role === 'agency_staff') {
      navigate('/agency/crm', { replace: true });
      return;
    }
    // Known-but-unlisted role — resolve via the shared role-home map so a
    // social sign-in is never stranded. roleHome falls back to /login only
    // when the role is genuinely unknown (fail-safe, not fail-closed).
    navigate(roleHome(role), { replace: true });
  }, [navigate]);

  const handleSocialResponse = useCallback(async (data, provider) => {
    if (!data) throw new Error('Empty response from server');

    // Case 1 & 2: server returned an existing / linked user + token.
    if (data.status === 'logged_in' || data.status === 'linked_and_logged_in') {
      setStatus('Loading your profile…');
      // This awaits token persistence AND /me bootstrap. Throws on
      // any hard failure so we render the recoverable error state.
      const fullUser = await loginWithSocial({ token: data.token, user: data.user });
      // Only NOW is it safe to celebrate.
      if (data.status === 'linked_and_logged_in' && data.message) {
        toast.success(data.message);
      } else {
        toast.success('Welcome back!');
      }
      routeByUser(fullUser);
      return;
    }
    // Case 3: brand-new user — needs to complete registration.
    if (data.status === 'needs_registration') {
      try {
        sessionStorage.setItem('social_registration_payload', JSON.stringify({
          provider,
          provider_user_id: data.provider_user_id,
          email: data.email,
          email_verified: data.email_verified,
          display_name: data.display_name,
          picture_url: data.picture_url,
        }));
      } catch (_e) { /* sessionStorage disabled */ }
      toast.info(data.message || 'Finish signing up to continue.');
      navigate('/register?social=1', { replace: true });
      return;
    }
    // Case 4: Facebook without email — verify separately.
    if (data.status === 'needs_email_verification') {
      toast.info(data.message || 'Please verify your email to continue.');
      navigate('/register?social=1&needs_email=1', { replace: true });
      return;
    }
    throw new Error(`Unexpected social auth status: ${data.status}`);
  }, [loginWithSocial, navigate, routeByUser]);

  const runFlow = useCallback(async () => {
    setErrorState(null);
    setStatus('Signing you in…');
    try {
      // Iter-411s — App-boot freshness. A stale PWA runtime after an
      // update can boot an obsolete AuthCallback bundle. This ensures
      // we reload to fresh JS BEFORE we exchange the OAuth session.
      // Non-blocking if the endpoint is unreachable.
      await ensureFreshRuntimeAtBoot({ scope: 'auth-callback' });

      const hash = location.hash || window.location.hash || '';
      if (hash.includes('session_id=')) {
        const sessionId = new URLSearchParams(hash.replace('#', '')).get('session_id');
        if (!sessionId) throw new Error('Missing session_id in Google callback URL');
        // Strip the fragment BEFORE the network call so a browser
        // back-nav can't replay it.
        history.replaceState({}, '', window.location.pathname);
        setStatus('Contacting iEvntür…');
        const res = await api.post('/auth/social/google/session', { session_id: sessionId });
        await handleSocialResponse(res.data, 'google');
        return;
      }
      const params = new URLSearchParams(location.search || window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      if (code) {
        const expected = sessionStorage.getItem('fb_oauth_state');
        const redirectUri = sessionStorage.getItem('fb_oauth_redirect_uri') || (window.location.origin + '/auth/callback');
        sessionStorage.removeItem('fb_oauth_state');
        sessionStorage.removeItem('fb_oauth_redirect_uri');
        if (!expected || expected !== state) {
          throw new Error('Facebook state mismatch — please try again.');
        }
        setStatus('Contacting iEvntür…');
        const res = await api.post('/auth/social/facebook/callback', {
          code, redirect_uri: redirectUri,
        });
        await handleSocialResponse(res.data, 'facebook');
        return;
      }
      throw new Error('No sign-in payload found. Please start again from the login page.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail)
        || (detail && typeof detail === 'object' && (detail.message || detail.detail))
        || e?.message
        || 'Sign in failed. Please try again.';
      setErrorState({
        message: msg,
        canRetry: e?.code === 'BOOTSTRAP_FAILED',  // only /me failures are safely retryable
      });
    }
  }, [location, handleSocialResponse]);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    runFlow();
  }, [runFlow]);

  const handleRetry = () => {
    // Retry ONLY the bootstrap — token is still in localStorage.
    // We do this by refreshing the page; AuthContext.initAuth will
    // rehydrate from the stored token+user and background-refresh /me.
    // Simpler than re-running the whole OAuth chain and safe because
    // the token is durable.
    window.location.assign('/talent/home');
  };

  const handleSignOutAndRetry = () => {
    logout();
    navigate('/login', { replace: true });
  };

  if (errorState) {
    return (
      <div
        className="min-h-screen bg-zinc-950 flex items-center justify-center px-4"
        data-testid="auth-callback-error"
      >
        <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-lg p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-white font-semibold text-lg" data-testid="auth-error-title">
                We couldn&apos;t finish signing you in
              </h2>
              <p className="text-zinc-400 text-sm mt-1" data-testid="auth-error-message">
                {errorState.message}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 pt-2">
            {errorState.canRetry && (
              <button
                type="button"
                onClick={handleRetry}
                className="w-full h-11 bg-cyan-500 hover:bg-cyan-600 text-black font-medium rounded-md inline-flex items-center justify-center gap-2 transition-all"
                data-testid="auth-error-retry-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Continue to my dashboard
              </button>
            )}
            <button
              type="button"
              onClick={handleSignOutAndRetry}
              className="w-full h-11 border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-white font-medium rounded-md inline-flex items-center justify-center gap-2 transition-all"
              data-testid="auth-error-signout-btn"
            >
              <LogIn className="w-4 h-4" />
              Sign out & back to login
            </button>
          </div>
          <p className="text-zinc-500 text-xs pt-2 border-t border-zinc-800">
            Your existing iEvntür account is safe. This error only means we
            couldn&apos;t finish loading your profile. Contact support if it
            keeps happening.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-zinc-950 flex items-center justify-center"
      data-testid="auth-callback-page"
    >
      <div className="text-center space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mx-auto" />
        <div className="text-zinc-300 text-sm" data-testid="auth-callback-status">
          {status}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
